import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { deleteCustomer, clearCustomerMessages } from "../../../store/customer/customerSlice";

const DeleteCustomerDialog = ({ open, onClose, customer, onSuccess }) => {
  const dispatch = useDispatch();
  const { isDeletingCustomer, deleteCustomerSuccess, deleteCustomerError } = useSelector(
    (state) => state.customer
  );

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("info");

  const handleDelete = () => {
    if (customer?.orgcode) {
      dispatch(deleteCustomer(customer.orgcode));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("info");
    dispatch(clearCustomerMessages());
  };

  // Handle success response - use data instead of message
  React.useEffect(() => {
    if (deleteCustomerSuccess) {
      // Use data field, fallback to message if data is not available
      const message = deleteCustomerSuccess.data || deleteCustomerSuccess.message || "Customer deleted successfully!";
      setSnackbarMessage(message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
        onSuccess();
        dispatch(clearCustomerMessages());
      }, 2000);
    }
  }, [deleteCustomerSuccess, onClose, onSuccess, dispatch]);

  // Handle error response - show actual backend error data
  React.useEffect(() => {
    if (deleteCustomerError) {
      // deleteCustomerError should contain the actual backend error data
      const errorMessage = typeof deleteCustomerError === "string" 
        ? deleteCustomerError 
        : (deleteCustomerError.data || deleteCustomerError.message || JSON.stringify(deleteCustomerError));
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [deleteCustomerError]);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="delete-customer-dialog"
        aria-describedby="dialog-for-deleting-customer"
      >
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 500 },
            maxWidth: 500,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header - Matching CustomerModal style */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#F5F5F5",
              padding: "16px 24px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#000",
                fontSize: "1.125rem",
              }}
            >
              Delete Customer
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                color: "#000",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: "#1e293b",
                fontSize: "0.875rem",
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to delete the customer{" "}
              <strong>{customer?.customer_name || customer?.orgcode}</strong>?
              <br />
              <br />
              This action cannot be undone.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isDeletingCustomer}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={isDeletingCustomer}
                startIcon={isDeletingCustomer ? <CircularProgress size={20} /> : null}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {isDeletingCustomer ? "Deleting..." : "Delete"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

DeleteCustomerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default DeleteCustomerDialog;

